import CATALOG_SOURCES from './catalog_sources.json' with { type: 'json' };
import { applySystemColorOptions, clearSystemColorOptions, isColorCatalogSelectable, systemColorCatalogIdsForAlias } from './system_color_usage.js';
export { configureSystemColorUsages, systemColorCatalogIds } from './system_color_usage.js';
import { RAL_CLASSIC_COLORS } from './ral_constants.js?v=20260706d';
import BODEN_TEXTURES from '../assets/textures/boden/textures.json' with { type: 'json' };
import WAND_TEXTURES from '../assets/textures/wand/textures.json' with { type: 'json' };
import DECKE_TEXTURES from '../assets/textures/decke/textures.json' with { type: 'json' };
import DOPPELBODEN_TEXTURES from '../assets/textures/doppelboden/textures.json' with { type: 'json' };
import DOPPELDECKE_TEXTURES from '../assets/textures/doppeldecke/textures.json' with { type: 'json' };
import { COLOR_USAGE_DEFAULT_CONFIG } from './color_usage_default_config.js?v=20260806a';
import {
  activeColorBindingContext,
  applyColorCatalogState,
  colorCatalogState,
  colorRevisionChangeVersion,
  colorRevisionEntry,
  colorStateSnapshot,
  currentColorChanges,
  currentColorRevision,
  colorStandardKeys,
  isColorEntryInactive,
  loadColorRevisions,
  loadColorStateSnapshot,
  requestColorRevisionData,
  sanitizeColorBinding
} from './color_revision_state.js';
export {
  activeColorBindingContext,
  colorCatalogState,
  configureColorBindingContext,
  configureColorRevisionDataListener,
  configureColorSelectionListener,
  notifyColorSelected,
  sanitizeColorBinding,
  withColorBindingContext
} from './color_revision_state.js';

export const COLOR_PICKER_DEFAULT_CATALOGS = Object.freeze(['standard', 'ral', 'ncs', 'decor', 'texturen']);
export const RAL_CLASSIC_MATERIAL_SURFACE = Object.freeze({
  kind: 'powder-coated-metal',
  finish: 'silk-gloss',
  label: 'Seidenglanz pulverbeschichtet',
  roughness: 0.46,
  metalness: 0.18,
  envMapIntensity: 0.62
});
export const PROMAT_VERZINKT_MATERIAL_SURFACE = Object.freeze({
  kind: 'zinc-coated-metal',
  finish: 'promatverzinkt',
  label: 'Promatverzinkt',
  roughness: 0.3,
  metalness: 0.52,
  envMapIntensity: 1.35
});
export const ALU_ELOX_MATERIAL_SURFACE = Object.freeze({
  kind: 'anodized-aluminium',
  finish: 'alu_elox',
  label: 'Alu eloxiert',
  roughness: 0.22,
  metalness: 0.72,
  envMapIntensity: 1.35
});
export const PA_SCHWARZ_MATERIAL_SURFACE = Object.freeze({
  kind: 'plastic',
  finish: 'pa_schwarz',
  label: 'PA schwarz',
  roughness: 0.68,
  metalness: 0.02,
  envMapIntensity: 0.42
});

export const COLOR_SPECIAL_COLORS = Object.freeze({
  galvanized: Object.freeze({
    catalog: 'special',
    code: 'galvanized',
    name: 'Feuerverzinkt',
    hex: '#C4C8C1',
    swatchImage: 'assets/materials/galvanized/verzinkt.jpg_460x460.jpg',
    texture: Object.freeze({
      image: 'assets/materials/galvanized/textures/Metal037_1K-JPG_Color.jpg',
      sampleWidthMm: 100,
      sampleHeightMm: 100,
      wrap: 'repeat'
    })
  }),
  promatverzinkt: Object.freeze({
    catalog: 'special',
    code: 'promatverzinkt',
    name: 'Promatverzinkt',
    hex: '#D8DCDF',
    swatchImage: 'assets/materials/promatverzinkt/textures/Metal049B.png',
    texture: Object.freeze({
      image: 'assets/materials/promatverzinkt/textures/Metal049B_1K-JPG_Color.jpg',
      sampleWidthMm: 100,
      sampleHeightMm: 100,
      wrap: 'repeat'
    }),
    materialSurface: PROMAT_VERZINKT_MATERIAL_SURFACE
  }),
  alu_elox: Object.freeze({
    catalog: 'special',
    code: 'alu_elox',
    name: 'Alu eloxiert',
    hex: '#E2E4DF',
    swatchImage: 'assets/materials/alu_elox/textures/Metal011.png',
    materialSurface: ALU_ELOX_MATERIAL_SURFACE
  }),
  pa_schwarz: Object.freeze({
    catalog: 'special',
    code: 'pa_schwarz',
    name: 'PA schwarz',
    hex: '#050505',
    swatchImage: 'assets/materials/pa_schwarz/textures/Plastic012B.png',
    texture: Object.freeze({
      image: 'assets/materials/pa_schwarz/textures/Plastic012B_1K-JPG_Color.jpg',
      sampleWidthMm: 100,
      sampleHeightMm: 100,
      wrap: 'repeat'
    }),
    materialSurface: PA_SCHWARZ_MATERIAL_SURFACE
  }),
  white_aluminium: Object.freeze({
    catalog: 'special',
    code: 'white_aluminium',
    name: 'Weissaluminium',
    hex: '#D7D9D4'
  })
});

function standardRalColor(code) {
  const key = String(code || '').trim();
  const entry = RAL_CLASSIC_COLORS[key] || [];
  return Object.freeze({
    code: `RAL${key}`,
    displayCode: `RAL ${key}`,
    name: entry[1] || `RAL ${key}`,
    hex: entry[0] || '#CFCCC8',
    group: 'ral',
    materialSurface: RAL_CLASSIC_MATERIAL_SURFACE
  });
}

export const STANDARD_COLOR_CATALOG_COLORS = Object.freeze([
  standardRalColor('9018'),
  standardRalColor('9011'),
  Object.freeze({
    code: 'galvanized',
    displayCode: 'FVZ',
    name: COLOR_SPECIAL_COLORS.galvanized.name,
    hex: COLOR_SPECIAL_COLORS.galvanized.hex,
    group: 'finish',
    swatchImage: COLOR_SPECIAL_COLORS.galvanized.swatchImage,
    texture: COLOR_SPECIAL_COLORS.galvanized.texture
  }),
  Object.freeze({
    code: 'promatverzinkt',
    displayCode: 'PVZ',
    name: COLOR_SPECIAL_COLORS.promatverzinkt.name,
    hex: COLOR_SPECIAL_COLORS.promatverzinkt.hex,
    group: 'finish',
    swatchImage: COLOR_SPECIAL_COLORS.promatverzinkt.swatchImage,
    texture: COLOR_SPECIAL_COLORS.promatverzinkt.texture,
    materialSurface: COLOR_SPECIAL_COLORS.promatverzinkt.materialSurface
  }),
  Object.freeze({
    code: 'alu_elox',
    displayCode: 'AE',
    name: COLOR_SPECIAL_COLORS.alu_elox.name,
    hex: COLOR_SPECIAL_COLORS.alu_elox.hex,
    group: 'finish',
    swatchImage: COLOR_SPECIAL_COLORS.alu_elox.swatchImage,
    texture: COLOR_SPECIAL_COLORS.alu_elox.texture,
    materialSurface: COLOR_SPECIAL_COLORS.alu_elox.materialSurface
  }),
  Object.freeze({
    code: 'pa_schwarz',
    displayCode: 'PA',
    name: COLOR_SPECIAL_COLORS.pa_schwarz.name,
    hex: COLOR_SPECIAL_COLORS.pa_schwarz.hex,
    group: 'finish',
    swatchImage: COLOR_SPECIAL_COLORS.pa_schwarz.swatchImage,
    texture: COLOR_SPECIAL_COLORS.pa_schwarz.texture,
    materialSurface: COLOR_SPECIAL_COLORS.pa_schwarz.materialSurface
  })
]);

// Texturkatalog „Material“ (F1: aufgelöste Standardmaterialien, F6: registriert).
export const MATERIAL_TEXTURE_CATALOG_COLORS = Object.freeze(
  STANDARD_COLOR_CATALOG_COLORS.filter(color => color.group === 'finish')
);

// Kategorien der Kataloge für die Zeilen im Colorpicker (F6). Die Kategorie der
// mitgelieferten Kataloge ist fest; eigene Kataloge bringen sie aus der DB mit.
export const COLOR_CATALOG_CATEGORIES = Object.freeze([
  Object.freeze({ key: 'standard', label: 'Standard' }),
  Object.freeze({ key: 'farben', label: 'Farben' }),
  Object.freeze({ key: 'dekore', label: 'Dekore' }),
  Object.freeze({ key: 'texturen', label: 'Texturen' })
]);

// Rückfall, solange der Katalogstand (DB-Haken) noch nicht geladen ist; entspricht
// dem Erstbestand backend/color_catalog_seed (Nutzerentscheid F9, 30.09.2026).
const DEFAULT_STANDARD_KEYS = Object.freeze(['ral:RAL9018', 'ral:RAL9011', 'decor:U12188', 'decor:U12290',
  'decor:W10141', 'texturen_material:galvanized', 'texturen_material:promatverzinkt']);

const catalogs = new Map();
const lazyCatalogLoaders = new Map();
const lazyCatalogPromises = new Map();
const catalogGenerations = new Map();

function registerLazyCatalogLoader(key, loader) {
  const normalizedKey = String(key || '').trim().toLowerCase();
  if (!normalizedKey || typeof loader !== 'function') return;
  lazyCatalogLoaders.set(normalizedKey, loader);
}

export function isColorCatalogLoaded(key) {
  const catalog = getColorCatalog(key);
  return Boolean(catalog && catalog.lazy !== true);
}

export function ensureColorCatalogLoaded(key) {
  const normalizedKey = String(key || '').trim().toLowerCase();
  if (!normalizedKey) return Promise.resolve(null);
  if (isColorCatalogLoaded(normalizedKey)) return Promise.resolve(getColorCatalog(normalizedKey));
  const loader = lazyCatalogLoaders.get(normalizedKey);
  if (!loader) return Promise.resolve(getColorCatalog(normalizedKey));
  if (!lazyCatalogPromises.has(normalizedKey)) {
    const generation = catalogGenerations.get(normalizedKey) || 0;
    const pending = Promise.resolve()
      .then(loader)
      .then(catalog => {
        if (generation !== (catalogGenerations.get(normalizedKey) || 0)) return ensureColorCatalogLoaded(normalizedKey);
        if (catalog) registerColorCatalog({ ...catalog, label: getColorCatalog(normalizedKey)?.label || catalog.label });
        return getColorCatalog(normalizedKey);
      })
      .finally(() => {
        if (lazyCatalogPromises.get(normalizedKey) === pending) lazyCatalogPromises.delete(normalizedKey);
      });
    lazyCatalogPromises.set(normalizedKey, pending);
  }
  return lazyCatalogPromises.get(normalizedKey);
}

export async function ensureColorCatalogsLoaded(keys = []) {
  const uniqueKeys = [...new Set((Array.isArray(keys) ? keys : [keys])
    .map(key => String(key || '').trim().toLowerCase())
    .filter(Boolean))];
  await Promise.all(uniqueKeys.map(ensureColorCatalogLoaded));
  return uniqueKeys.map(getColorCatalog).filter(Boolean);
}

function colorCatalogKeyHint(key) {
  return /(?:decor|dekor|color|colour|farbe|material|finish|oberflaeche|oberfläche)/i.test(String(key || ''));
}

function collectRequiredColorCatalogs(value, result, parentKey = '', seen = new Set()) {
  if (value == null) return;
  if (typeof value === 'string') {
    const raw = value.trim();
    if (normalizeNcsColorCode(raw)) result.add('ncs');
    if (colorCatalogKeyHint(parentKey) && /^[A-Z]\d{5}$/i.test(raw)) result.add('decor');
    return;
  }
  if (typeof value !== 'object' || seen.has(value)) return;
  seen.add(value);
  const catalog = String(value.catalog || value.catalogKey || '').trim().toLowerCase();
  if (catalog === 'ncs' || catalog === 'decor' || getColorCatalog(catalog)?.source === 'managed') {
    result.add(catalog);
    return;
  }
  if (Array.isArray(value)) {
    value.forEach(entry => collectRequiredColorCatalogs(entry, result, parentKey, seen));
    return;
  }
  Object.entries(value).forEach(([key, entry]) => {
    collectRequiredColorCatalogs(entry, result, key, seen);
  });
}

export function requiredColorCatalogsForProject(project) {
  const result = new Set();
  collectRequiredColorCatalogs(project, result);
  return [...result];
}

// Lädt vor der Normalisierung eines Auftrags auch die älteren Revisionen, die
// sein Katalogstand und seine ausdrücklichen Bindungen brauchen (F2). Ein
// Fehler dabei blockiert das Laden nicht; die Werte bleiben dann beim Erstbestand
// bzw. beim aktuellen Stand und werden nach erfolgreichem Nachladen neu gezeichnet.
export async function ensureColorCatalogsForProject(project) {
  const loaded = await ensureColorCatalogsLoaded(requiredColorCatalogsForProject(project));
  try {
    await ensureColorRevisionsForBinding(project?.colorCatalogBinding);
  } catch (error) {
    console.warn('Farbkatalog-Revisionen des Auftrags konnten nicht geladen werden.', error);
  }
  return loaded;
}

export async function ensureColorRevisionsForBinding(value) {
  const binding = sanitizeColorBinding(value);
  if (!binding) return;
  const current = colorCatalogState();
  const jobs = [];
  if (binding.state !== null && current !== null && binding.state < current) jobs.push(loadColorStateSnapshot(binding.state));
  const refs = Object.entries(binding.bindings)
    .filter(([key, revision]) => revision !== 1 && currentColorRevision(key)?.revision !== revision)
    .map(([key, revision]) => `${key}@${revision}`);
  if (refs.length) jobs.push(loadColorRevisions(refs));
  await Promise.all(jobs);
}

export function normalizeColorCode(code) {
  return String(code || '').trim().replace(/\s+/g, ' ').toUpperCase();
}

export function normalizeRalColorCode(code) {
  const raw = String(code || '').trim().toUpperCase().replace(/\s+/g, '');
  const match = raw.match(/^(?:RAL)?(\d{4})$/);
  return match ? `RAL${match[1]}` : '';
}

export function normalizeNcsColorCode(code) {
  const raw = normalizeColorCode(code).replace(/^NCS\s+/, '');
  const match = raw.match(/^S\s+(\d{4})-([A-Z0-9]+)$/) || raw.match(/^(\d{4})-([A-Z0-9]+)$/);
  return match ? `NCS S ${match[1]}-${match[2]}` : '';
}

function normalizeCatalogColorCode(catalogKey, code) {
  const key = String(catalogKey || '').trim().toLowerCase();
  if (key === 'ral' || key === 'standard') {
    const ral = normalizeRalColorCode(code);
    if (ral) return ral;
  }
  if (key === 'ncs') {
    const ncs = normalizeNcsColorCode(code);
    if (ncs) return ncs;
  }
  return normalizeColorCode(code);
}

function ralEntries(colors) {
  return Object.entries(colors || {})
    .filter(([code]) => /^\d{4}$/.test(code))
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([code, [hex, name]]) => ({
      catalog: 'ral',
      code: `RAL${code}`,
      displayCode: `RAL ${code}`,
      name,
      hex,
      group: code.slice(0, 1),
      nuance: code.slice(1),
      materialSurface: RAL_CLASSIC_MATERIAL_SURFACE
    }));
}

function flatEntries(catalog) {
  const colors = catalog?.colors;
  if (catalog?.key === 'ral') return ralEntries(colors);
  if (Array.isArray(colors)) {
    return colors
      .map(color => {
        // A texture-catalog entry (assets/textures/<category>/textures.json)
        // only ships `paths` (full PBR map set, used to build the real 3D
        // material) — derive the 2D swatch preview from its basecolor map
        // instead of requiring every manifest entry to repeat the same path.
        const swatchImage = color.swatchImage || color.image || color.paths?.basecolor || '';
        const texture = color.texture || (color.paths?.basecolor ? {
          image: color.paths.basecolor,
          sampleWidthMm: 900,
          sampleHeightMm: 900,
          wrap: 'repeat'
        } : null);
        return {
          catalog: catalog.key,
          ...(color.active === undefined ? {} : { active: color.active }),
          code: catalog.preserveCodeCase
            ? String(color.code || color.key || '').trim()
            : normalizeColorCode(color.code || color.key),
          displayCode: color.displayCode || normalizeColorCode(color.code || color.key),
          name: color.name || color.label || normalizeColorCode(color.code || color.key),
          hex: color.hex || color.color || '#CFCCC8',
          group: color.group || '',
          nuance: color.nuance || '',
          swatchImage,
          texture,
          paths: color.paths || null,
          repeat: Array.isArray(color.repeat) ? color.repeat : null,
          defaultColor: color.defaultColor || '',
          materialType: color.materialType || color.type || '',
          materialSurface: color.materialSurface || null,
          ncs: color.ncs || '',
          sourceUrl: color.sourceUrl || ''
        };
      })
      .filter(color => color.code);
  }
  return Object.entries(colors || {})
    .map(([code, entry]) => {
      const hex = Array.isArray(entry) ? entry[0] : entry?.hex || entry?.color;
      const name = Array.isArray(entry) ? entry[1] : entry?.name || entry?.label;
      return {
        catalog: catalog.key,
        code: catalog.preserveCodeCase ? String(code || '').trim() : normalizeColorCode(code),
        displayCode: normalizeColorCode(code),
        name: name || normalizeColorCode(code),
        hex: hex || '#CFCCC8',
        group: '',
        nuance: '',
        swatchImage: entry?.swatchImage || entry?.image || '',
        texture: entry?.texture || null,
        materialType: entry?.materialType || entry?.type || '',
        materialSurface: entry?.materialSurface || null,
        ncs: entry?.ncs || '',
        sourceUrl: entry?.sourceUrl || ''
      };
    });
}

function ncsEntries(colors) {
  return (Array.isArray(colors) ? colors : [])
    .map(color => ({
      catalog: 'ncs',
      code: normalizeNcsColorCode(color.code),
      displayCode: normalizeNcsColorCode(color.code),
      name: color.name || normalizeNcsColorCode(color.code),
      hex: color.hex || '#CFCCC8',
      group: color.group || 'neutral',
      nuance: color.nuance || '',
      hue: color.hue || ''
    }))
    .filter(color => color.code);
}

// A "group" catalog (e.g. the "Texturen" tab) isn't a color list itself —
// it's a picker-UI grouping of other, already-registered real catalogs
// (its `members`, by key). Clicking its tab reveals a row of buttons, one
// per member, instead of a swatch list (see color_picker.js).
function normalizeCatalog(catalog) {
  if (!catalog?.key) return null;
  const key = String(catalog.key).trim().toLowerCase();
  if (catalog.type === 'group') {
    return {
      ...catalog,
      key,
      members: Array.isArray(catalog.members) ? catalog.members : [],
      colors: [],
      colorByCode: new Map()
    };
  }
  const colors = catalog.type === 'structured'
    ? ncsEntries(catalog.colors)
    : flatEntries({ ...catalog, key });
  return {
    ...catalog,
    key,
    colors,
    colorByCode: new Map(colors.map(color => [normalizeCatalogColorCode(key, color.code), color]))
  };
}

export function registerColorCatalog(catalog) {
  const normalized = normalizeCatalog(catalog);
  if (!normalized) return null;
  catalogs.set(normalized.key, normalized);
  return normalized;
}

export function getColorCatalog(key) {
  return catalogs.get(String(key || '').trim().toLowerCase()) || null;
}

export function listColorCatalogs(keys = null) {
  const allowed = Array.isArray(keys) ? keys : COLOR_PICKER_DEFAULT_CATALOGS;
  return allowed.filter(isColorCatalogSelectable).map(getColorCatalog).filter(Boolean);
}

// usageKey -> catalog keys[], populated by applyColorUsageConfig() at boot
// (see loadAndApplyColorUsageConfig / app_bootstrap.js). Which catalogs a
// given colorpicker instance offers is now purely DB-driven — see
// colorPickerAllowedCatalogsForUsage below and the admin modal
// (js/core/app/color_catalog_admin_modal.js) that edits this table.
const colorUsageCatalogKeys = new Map();

export function applyColorUsageConfig(config = {}) {
  const usages = Array.isArray(config.usages) ? config.usages : [];
  const usageCatalogs = Array.isArray(config.usageCatalogs) ? config.usageCatalogs : [];

  const catalogKeysByUsageKey = new Map();
  for (const link of usageCatalogs) {
    const usageKey = String(link?.usageKey || '').trim();
    const katalogKey = String(link?.katalogKey || '').trim();
    if (!usageKey || !katalogKey) continue;
    if (!catalogKeysByUsageKey.has(usageKey)) catalogKeysByUsageKey.set(usageKey, []);
    catalogKeysByUsageKey.get(usageKey).push(katalogKey);
  }

  colorUsageCatalogKeys.clear();
  for (const usage of usages) {
    const usageKey = String(usage?.usage_key || '').trim();
    if (!usageKey) continue;
    colorUsageCatalogKeys.set(usageKey, usage.active_flag === false ? [] : catalogKeysByUsageKey.get(usageKey) || []);
  }
}

// Legacy callers use only aliases declared by discovery; native callers use
// the owner-bound runtime port. Missing/empty configurations never expand access.
export function colorPickerAllowedCatalogsForUsage(usageKey) {
  const key = String(usageKey || '').trim();
  return systemColorCatalogIdsForAlias(key) ?? [...(colorUsageCatalogKeys.get(key) || [])];
}

let colorConfigRequest = 0;
export async function loadAndApplyColorUsageConfig() {
  const request = ++colorConfigRequest;
  clearSystemColorOptions();
  const read = async path => {
    const response = await fetch(path);
    if (!response.ok) throw new Error('Farbkonfiguration nicht verfügbar.');
    const payload = await response.json();
    if (!payload?.ok) throw new Error('Ungültige Farbkonfiguration.');
    return payload;
  };
  const results = await Promise.allSettled([read('/api/color-usage-config'), read('/api/system-options'),
    read('/api/color-catalogs/state')]);
  if (request !== colorConfigRequest) return null;
  if (results[0].status === 'fulfilled') applyColorCatalogMetadata(results[0].value.catalogs || []);
  // Katalogstand und aktuelle Abweichungen vom Erstbestand (F2). Ohne Antwort
  // bleiben die Codedateien (Revision 1) maßgebend.
  const touched = results[2].status === 'fulfilled' ? applyColorCatalogState(results[2].value) : null;
  if (results[2].status === 'rejected') console.warn('Farbkatalog-Revisionen sind nicht verfügbar. Es gilt der Erstbestand.', results[2].reason);
  if (touched?.length && typeof document !== 'undefined') {
    // Die Ansicht „Standard“ setzt sich aus allen Katalogen zusammen.
    const keys = new Set([...touched, 'standard']);
    document.dispatchEvent(new CustomEvent('ks-color-catalogs-changed', { detail: { keys: [...keys] } }));
  }
  applyColorUsageConfig(results[0].status === 'fulfilled' ? results[0].value : COLOR_USAGE_DEFAULT_CONFIG);
  let optionsLoaded = false;
  try {
    if (results[1].status === 'rejected') throw results[1].reason;
    applySystemColorOptions(results[1].value);
    optionsLoaded = true;
  } catch (error) {
    clearSystemColorOptions();
    console.warn('System-Farbverwendungen sind nicht verfügbar. Bestehende Farben bleiben erhalten.', error);
  }
  return { catalogsLoaded: results[0].status === 'fulfilled', optionsLoaded, revisionsLoaded: Boolean(touched) };
}

function managedCategory(value) {
  return ['farben', 'dekore', 'texturen'].includes(value) ? value : 'farben';
}

// Metadata is small; entry payloads remain lazy, including administrated catalogs.
export function applyColorCatalogMetadata(rows = []) {
  const changed = [];
  for (const row of rows) {
    const key = String(row.katalog_key || '').trim();
    if (!/^[a-z][a-z0-9_-]*$/.test(key)) continue;
    const current = getColorCatalog(key);
    if (Object.hasOwn(CATALOG_SOURCES, key)) {
      if (current) current.label = row.label || current.label;
      continue;
    }
    const revision = Number(row.entry_revision || 0);
    if (current?.source === 'managed' && current.revision === revision) {
      current.label = row.label || current.label;
      current.category = managedCategory(row.kategorie);
      continue;
    }
    if (current && current.source !== 'managed') continue;
    catalogGenerations.set(key, (catalogGenerations.get(key) || 0) + 1);
    lazyCatalogPromises.delete(key);
    registerColorCatalog({ key, label: row.label || key, category: managedCategory(row.kategorie), type: 'flat',
      source: 'managed', revision, lazy: true, colors: [] });
    registerLazyCatalogLoader(key, async () => {
      const response = await fetch(`/api/color-catalogs/${encodeURIComponent(key)}/entries`, { credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) throw new Error('Katalogeinträge nicht verfügbar.');
      const payload = await response.json();
      const data = payload.catalog;
      if (!payload.ok || data?.key !== key || data.source !== 'managed' || !Array.isArray(data.entries) || data.revision < revision) throw new Error('Ungültige Katalogantwort.');
      return { key, label: data.label, category: getColorCatalog(key)?.category || 'farben', type: 'flat', source: 'managed', revision: data.revision,
        colors: data.entries.map(entry => ({ ...entry, swatchImage: entry.swatchImage || entry.texture?.image || '' })) };
    });
    changed.push(key);
  }
  if (changed.length && typeof document !== 'undefined') {
    document.dispatchEvent(new CustomEvent('ks-color-catalogs-changed', { detail: { keys: changed } }));
  }
  return changed;
}

// --- Revisionen (docs/architecture/color-catalog-revisions.md, F2) ---------
// Die registrierten Einträge der Codedateien sind Revision 1. Abweichungen aus
// dem Backend werden nur über die folgenden Helfer eingeblendet: `findColor`
// löst im Kontext des geöffneten Auftrags auf (Katalogstand + ausdrückliche
// Bindungen, nie live beim Laden), `findCurrentColor` und `colorCatalogEntries`
// liefern den aktuellen Stand für neue Auswahlen.
const REVISIONED_CATALOGS = new Set(['standard', 'ral', 'ncs', 'decor', 'texturen_boden', 'texturen_wand',
  'texturen_decke', 'texturen_doppelboden', 'texturen_doppeldecke', 'texturen_material']);
// Die bisherigen Standardmaterialien sind aufgelöst (F1): gleiche Revisionen wie
// RAL Classic bzw. der Texturkatalog „Material“. Die Ausgabe behält bis zur
// Pickerumstellung (F6) den angefragten Katalogschlüssel.
const STANDARD_REVISION_ALIASES = Object.freeze({
  RAL9018: 'ral', RAL9011: 'ral',
  galvanized: 'texturen_material', promatverzinkt: 'texturen_material', alu_elox: 'texturen_material', pa_schwarz: 'texturen_material'
});
const shapedRevisionEntries = new Map();
let changedOnlyIndex = { version: -1, map: new Map() };

function revisionKeyFor(catalogKey, code) {
  if (catalogKey === 'standard' && Object.hasOwn(STANDARD_REVISION_ALIASES, code)) return `${STANDARD_REVISION_ALIASES[code]}:${code}`;
  return `${catalogKey}:${code}`;
}

// Einträge, die es nur im Backend gibt (z. B. spätere Importe), nach Lookup-Code.
function changedOnlyKey(catalog, normalizedCode) {
  const version = colorRevisionChangeVersion();
  if (changedOnlyIndex.version !== version) {
    const map = new Map();
    for (const key of currentColorChanges().keys()) {
      const separator = key.indexOf(':');
      const catalogKey = key.slice(0, separator);
      const target = getColorCatalog(catalogKey);
      if (target) map.set(`${catalogKey}|${normalizeCatalogColorCode(catalogKey, key.slice(separator + 1))}`, key);
    }
    changedOnlyIndex = { version, map };
  }
  return changedOnlyIndex.map.get(`${catalog.key}|${normalizedCode}`) || null;
}

function currentRevisionNumber(key) {
  return currentColorRevision(key)?.revision || 1;
}

function targetRevisionFor(key, context) {
  if (!context) return currentRevisionNumber(key);
  if (Object.hasOwn(context.bindings, key)) return context.bindings[key];
  if (context.state === null) return 1;
  const current = colorCatalogState();
  // Unbekannter Katalogstand (Backend nicht erreichbar): keine Abweichungen bekannt.
  if (current === null || context.state >= current) return currentRevisionNumber(key);
  const snapshot = colorStateSnapshot(context.state);
  if (snapshot) return snapshot.get(key)?.revision || 1;
  // Normalerweise vor dem Laden bereitgestellt; sonst im Hintergrund nachladen.
  requestColorRevisionData({ at: context.state });
  return currentRevisionNumber(key);
}

function shapedRevisionEntry(catalogKey, key, revision, entry) {
  const cacheKey = `${catalogKey}|${key}@${revision}`;
  let shaped = shapedRevisionEntries.get(cacheKey);
  if (!shaped || shaped.source !== entry) {
    shaped = { source: entry, value: Object.freeze({ ...entry, catalog: catalogKey }) };
    shapedRevisionEntries.set(cacheKey, shaped);
  }
  return shaped.value;
}

function revisionedColor(catalog, code, context) {
  const normalizedCode = normalizeCatalogColorCode(catalog.key, code);
  const baseline = catalog.colorByCode.get(normalizedCode) || null;
  // „Standard“ ist seit F6 eine Ansicht. Gespeicherte `standard`-Verweise lösen in
  // ihren echten Katalog auf (RAL Classic bzw. Material), gleiche Revisionen wie F2.
  if (catalog.key === 'standard' && baseline && Object.hasOwn(STANDARD_REVISION_ALIASES, baseline.code)) {
    const target = getColorCatalog(STANDARD_REVISION_ALIASES[baseline.code]);
    if (target) return revisionedColor(target, baseline.code, context);
  }
  if (!REVISIONED_CATALOGS.has(catalog.key) || catalog.type === 'group') return baseline;
  const key = baseline ? revisionKeyFor(catalog.key, baseline.code) : changedOnlyKey(catalog, normalizedCode);
  if (!key) return baseline;
  const revision = targetRevisionFor(key, context);
  if (revision === 1 && baseline) return baseline;
  const change = currentColorRevision(key);
  if (change?.revision === revision) return shapedRevisionEntry(catalog.key, key, revision, change.entry);
  const stored = colorRevisionEntry(key, revision);
  if (stored) return shapedRevisionEntry(catalog.key, key, revision, stored);
  requestColorRevisionData({ refs: [`${key}@${revision}`] });
  return change ? shapedRevisionEntry(catalog.key, key, change.revision, change.entry) : baseline;
}

// Revisionsschlüssel eines Farbwerts (für ausdrückliche Bindungen), sonst null.
export function colorRevisionKey(value) {
  const normalized = normalizeColorValue(value, { includeSpecial: true });
  const catalog = getColorCatalog(normalized?.catalog);
  if (!catalog || !REVISIONED_CATALOGS.has(catalog.key) || catalog.type === 'group') return null;
  const normalizedCode = normalizeCatalogColorCode(catalog.key, normalized.code);
  const baseline = catalog.colorByCode.get(normalizedCode);
  return baseline ? revisionKeyFor(catalog.key, baseline.code) : changedOnlyKey(catalog, normalizedCode);
}

export function currentColorRevisionNumber(key) {
  return currentRevisionNumber(String(key || ''));
}

// Revision, mit der ein Schlüssel im gegebenen Kontext dargestellt wird.
export function resolvedColorRevisionNumber(key, context = activeColorBindingContext()) {
  return targetRevisionFor(String(key || ''), context);
}

function isListedEntry(catalogKey, entry) {
  return Boolean(entry) && entry.active !== false && !isColorEntryInactive(`${catalogKey}:${entry.code}`);
}

export function colorCatalogEntries(catalogKey) {
  const catalog = getColorCatalog(catalogKey);
  if (catalog?.key === 'standard') return colorStandardEntries();
  const colors = catalog?.colors || [];
  if (!catalog || !REVISIONED_CATALOGS.has(catalog.key) || !currentColorChanges().size) {
    return colors.filter(entry => isListedEntry(catalog?.key, entry));
  }
  const current = colors.map(color => revisionedColor(catalog, color.code, null));
  const listed = new Set(colors.map(color => revisionKeyFor(catalog.key, color.code)));
  for (const [key, change] of currentColorChanges()) {
    if (listed.has(key) || !key.startsWith(`${catalog.key}:`)) continue;
    current.push(shapedRevisionEntry(catalog.key, key, change.revision, change.entry));
  }
  return current.filter(entry => isListedEntry(catalog.key, entry));
}

// Echte Kataloge einer Freigabeliste: Gruppen aufgelöst, „Standard“ ausgelassen,
// nur auswählbare Kataloge.
export function expandColorCatalogKeys(keys = COLOR_PICKER_DEFAULT_CATALOGS) {
  const result = [];
  for (const catalog of listColorCatalogs(Array.isArray(keys) ? keys : [])) {
    if (catalog.key === 'standard') continue;
    const members = catalog.type === 'group' ? listColorCatalogs(catalog.members || []) : [catalog];
    for (const member of members) if (!result.includes(member.key)) result.push(member.key);
  }
  return result;
}

function standardKeysFor(allowedCatalogKeys) {
  const allowed = allowedCatalogKeys == null ? null : new Set(expandColorCatalogKeys(allowedCatalogKeys));
  return (colorStandardKeys() ?? DEFAULT_STANDARD_KEYS).filter(key => {
    const catalogKey = key.slice(0, key.indexOf(':'));
    return (!allowed || allowed.has(catalogKey)) && isColorCatalogSelectable(catalogKey) && getColorCatalog(catalogKey);
  });
}

// Ansicht „Standard“ (F6): alle Einträge mit Haken „Standard“ aus den Katalogen,
// die der Picker freigibt. Ohne Freigabeliste alle auswählbaren Kataloge.
export function colorStandardEntries(allowedCatalogKeys = null) {
  const result = [];
  for (const key of standardKeysFor(allowedCatalogKeys)) {
    if (isColorEntryInactive(key)) continue;
    const separator = key.indexOf(':');
    const catalogKey = key.slice(0, separator);
    const color = findCurrentColor(catalogKey, key.slice(separator + 1));
    if (isListedEntry(catalogKey, color)) result.push(color);
  }
  return result;
}

// Kataloge, die für die Ansicht „Standard“ geladen sein müssen (z. B. Dekore).
export function colorStandardCatalogKeys(allowedCatalogKeys = null) {
  return [...new Set(standardKeysFor(allowedCatalogKeys).map(key => key.slice(0, key.indexOf(':'))))];
}

export function colorCatalogCategory(catalogKey) {
  const catalog = getColorCatalog(catalogKey);
  if (!catalog) return '';
  if (catalog.key === 'standard') return 'standard';
  return catalog.category || 'farben';
}

export function findColor(catalogKey, code) {
  const catalog = getColorCatalog(catalogKey);
  if (!catalog) return null;
  return revisionedColor(catalog, code, activeColorBindingContext());
}

// Aktueller Katalogstand ohne Auftragsbezug, z. B. für eine neue Auswahl im Picker.
export function findCurrentColor(catalogKey, code) {
  const catalog = getColorCatalog(catalogKey);
  if (!catalog) return null;
  return revisionedColor(catalog, code, null);
}

export function firstColor(catalogKey) {
  return colorCatalogEntries(catalogKey)[0] || null;
}

const defaultCoreCatalogRegistration = Object.freeze({
  registerCatalog: registerColorCatalog,
  getCatalog: getColorCatalog,
  listCatalogs: listColorCatalogs,
  allowedCatalogKeys: colorPickerAllowedCatalogsForUsage,
  entries: colorCatalogEntries,
  findColor,
  firstColor,
  normalizeColorCode,
  normalizeRalColorCode,
  normalizeNcsColorCode
});

export function registerCoreColorCatalogs(registry = defaultCoreCatalogRegistration) {
  registry.registerCatalog({
    key: 'standard',
    label: 'Standard',
    category: 'standard',
    view: true,
    type: 'flat',
    preserveCodeCase: true,
    colors: STANDARD_COLOR_CATALOG_COLORS
  });

  registry.registerCatalog({
    key: 'ral',
    label: 'RAL Classic',
    category: 'farben',
    default: true,
    type: 'flat',
    colors: RAL_CLASSIC_COLORS
  });

  // Ziel der aufgelösten Standardmaterialien; wird mit „Standard“ registriert,
  // damit gespeicherte `standard`-Verweise immer denselben Katalog erreichen.
  registry.registerCatalog({
    key: 'texturen_material',
    label: 'Material',
    category: 'texturen',
    type: 'flat',
    preserveCodeCase: true,
    colors: MATERIAL_TEXTURE_CATALOG_COLORS
  });

  registry.registerCatalog({
    key: 'ncs',
    label: 'NCS',
    category: 'farben',
    type: 'structured',
    lazy: true,
    groups: [],
    colors: []
  });

  registry.registerCatalog({
    key: 'decor',
    label: 'Pfleiderer',
    category: 'dekore',
    type: 'flat',
    lazy: true,
    preserveCodeCase: true,
    colors: []
  });

  registerLazyCatalogLoader('ncs', async () => {
    const { NCS_COLOR_GROUPS, NCS_COLORS } = await import('./ncs_constants.js?v=20260710a');
    return {
      key: 'ncs',
      label: 'NCS',
      category: 'farben',
      type: 'structured',
      groups: NCS_COLOR_GROUPS,
      colors: NCS_COLORS
    };
  });
  registerLazyCatalogLoader('decor', async () => {
    const { PFLEIDERER_DECOR_COLORS } = await import('./pfleiderer_decor_catalog.js?v=20260710a');
    return {
      key: 'decor',
      label: 'Pfleiderer',
      category: 'dekore',
      type: 'flat',
      preserveCodeCase: true,
      colors: PFLEIDERER_DECOR_COLORS
    };
  });

  return registry;
}

// The 5 real texture catalogs live under assets/textures/<category>/ — drop
// image files into a category folder and add one entry to its textures.json
// manifest (same convention as the existing assets/materials/materials.json)
// to make it show up. Wand/Decke/Doppelboden/Doppeldecke start empty and are
// ready to receive real textures later; only Boden has content today.
export function registerTextureColorCatalogs(registry = defaultCoreCatalogRegistration) {
  const texture = (key, label, colors) => ({ key, label, category: 'texturen', type: 'flat', preserveCodeCase: true, colors });
  registry.registerCatalog(texture('texturen_boden', 'Boden', BODEN_TEXTURES));
  registry.registerCatalog(texture('texturen_wand', 'Wand', WAND_TEXTURES));
  registry.registerCatalog(texture('texturen_decke', 'Decke', DECKE_TEXTURES));
  registry.registerCatalog(texture('texturen_doppelboden', 'Doppelboden', DOPPELBODEN_TEXTURES));
  registry.registerCatalog(texture('texturen_doppeldecke', 'Doppeldecke', DOPPELDECKE_TEXTURES));

  registry.registerCatalog({
    key: 'texturen',
    label: 'Texturen',
    category: 'texturen',
    type: 'group',
    members: ['texturen_boden', 'texturen_doppelboden', 'texturen_decke', 'texturen_doppeldecke', 'texturen_wand', 'texturen_material']
  });

  return registry;
}

function normalizedColorPayload(color) {
  if (!color) return null;
  return {
    catalog: color.catalog,
    code: color.code,
    displayCode: color.displayCode || color.code,
    name: color.name,
    hex: color.hex,
    group: color.group || '',
    nuance: color.nuance || '',
    hue: color.hue || '',
    swatchImage: color.swatchImage || '',
    texture: color.texture || null,
    materialType: color.materialType || '',
    materialSurface: color.materialSurface || null,
    ncs: color.ncs || '',
    sourceUrl: color.sourceUrl || ''
  };
}

export function normalizeColorValue(value, options = {}) {
  if (value == null || value === '' || value === false) return null;
  if (typeof value === 'object') {
    const rawCatalog = String(value.catalog || value.catalogKey || '').trim().toLowerCase();
    if (getColorCatalog(rawCatalog)?.source === 'managed' && value.hex && value.code) return normalizedColorPayload({ ...value, catalog: rawCatalog });
    const migratedCatalog = rawCatalog === 'material' || rawCatalog === 'special' ? 'standard' : rawCatalog;
    const catalog = rawCatalog || inferColorCatalog(value.code);
    const found = migratedCatalog
      ? findColor(migratedCatalog, value.code) || findColor('standard', value.code)
      : findColor(inferColorCatalog(value.code), value.code);
    if (found) return normalizedColorPayload(found);
    if (value.hex && value.code) {
      return {
        catalog: migratedCatalog || catalog || 'custom',
        code: String(value.code).trim(),
        name: value.name || String(value.code).trim(),
        hex: value.hex,
        swatchImage: value.swatchImage || '',
        texture: value.texture || null,
        materialSurface: value.materialSurface || null,
        ncs: value.ncs || '',
        sourceUrl: value.sourceUrl || ''
      };
    }
    return null;
  }

  const raw = String(value).trim();
  if (!raw || /^(default|standard|none|null)$/i.test(raw)) return null;
  const standard = findColor('standard', raw);
  if (standard) {
    return normalizedColorPayload(standard);
  }
  const specialKey = raw.toLowerCase();
  if (COLOR_SPECIAL_COLORS[specialKey] && options.includeSpecial !== false) {
    return normalizedColorPayload(COLOR_SPECIAL_COLORS[specialKey]);
  }
  const ralCode = normalizeRalColorCode(raw);
  if (ralCode) {
    const found = findColor('ral', ralCode);
    if (found) return normalizedColorPayload(found);
  }
  const ncsCode = normalizeNcsColorCode(raw);
  if (ncsCode) {
    const found = findColor('ncs', ncsCode);
    if (found) return normalizedColorPayload(found);
  }
  const decor = findColor('decor', raw);
  if (decor) return normalizedColorPayload(decor);
  if (/^#[0-9a-f]{3}([0-9a-f]{3})?$/i.test(raw) || /^rgba?\(/i.test(raw)) {
    return { catalog: 'custom', code: raw, name: raw, hex: raw };
  }
  return null;
}

export function inferColorCatalog(code) {
  const raw = String(code || '').trim();
  if (findColor('standard', raw)) return 'standard';
  if (normalizeRalColorCode(raw)) return 'ral';
  if (normalizeNcsColorCode(raw)) return 'ncs';
  if (findColor('decor', raw)) return 'decor';
  return '';
}

export function resolveColorHex(value, fallback = '#CFCCC8') {
  return normalizeColorValue(value)?.hex || fallback;
}

export function resolveColorLabel(value, fallback = '') {
  const normalized = normalizeColorValue(value);
  if (!normalized) return fallback;
  const ralCode = normalizeRalColorCode(normalized.code);
  if (ralCode) {
    return `${ralCode.replace(/^RAL/, 'RAL ')}${normalized.name ? ` ${normalized.name}` : ''}`;
  }
  if (normalizeNcsColorCode(normalized.code)) return normalized.name || normalized.code;
  if (normalized.catalog === 'decor') {
    return `${normalized.code}${normalized.name ? ` ${normalized.name}` : ''}`;
  }
  return normalized.name || normalized.code || fallback;
}

export function colorValueKey(value) {
  const normalized = normalizeColorValue(value);
  return normalized ? `${normalized.catalog}:${normalized.code}` : '';
}

export function colorValueCode(value) {
  return normalizeColorValue(value, { includeSpecial: true })?.code || String(value || '').trim();
}

export function isColorValueCode(value, code) {
  return colorValueCode(value).toLowerCase() === String(code || '').trim().toLowerCase();
}

export function serializeColorValue(value, options = {}) {
  const normalized = normalizeColorValue(value, { includeSpecial: true });
  if (!normalized) return null;
  if (options.legacyString || options.legacy) return normalized.code;
  return {
    catalog: normalized.catalog,
    code: normalized.code,
    name: normalized.name,
    hex: normalized.hex,
    swatchImage: normalized.swatchImage || '',
    texture: normalized.texture || null,
    materialSurface: normalized.materialSurface || null,
    ncs: normalized.ncs || '',
    sourceUrl: normalized.sourceUrl || ''
  };
}
